import { z } from "zod";
import { PERMISSIONS } from "../auth/permissions";
import type { RequestVisibility } from "../auth/scope";
import { ENGAGEMENT_SHAPES, SIZE_TIERS } from "../intake/classification";
import {
  messageViewSchema,
  PRIVATE_STATUSES,
  REQUEST_STATUSES,
  type RequestStatus,
  requestPanelSchema,
} from "./schemas";

/**
 * Buyer workspace (WP-3.1, ADR-0022): the status workflow, who may act, the queue and the review
 * page. Shared so the API enforces and the web app shows exactly the same rules.
 */

/** Where a buyer may move a request from each status. APPROVED / REJECTED are final (Q2 open). */
export const REQUEST_TRANSITIONS: Readonly<Record<RequestStatus, readonly RequestStatus[]>> = {
  DRAFT: [],
  SUBMITTED: ["IN_REVIEW"],
  IN_REVIEW: ["RETURNED_TO_REQUESTER", "SOURCING", "APPROVED", "REJECTED"],
  // The requester answers and resubmits; that, not the buyer, moves it back to IN_REVIEW.
  RETURNED_TO_REQUESTER: [],
  SOURCING: ["IN_REVIEW", "APPROVED", "REJECTED"],
  APPROVED: [],
  REJECTED: [],
  // Closed by the watcher (ADR-0024); only its requester reopens it.
  ABANDONED: [],
};

/** Transitions whose note is required: the questions for the requester, the reason for a rejection. */
export const NOTE_REQUIRED: ReadonlySet<RequestStatus> = new Set(["RETURNED_TO_REQUESTER", "REJECTED"]);

/** The queue's default: requests with work left for procurement. */
export const OPEN_WORK_STATUSES = ["SUBMITTED", "IN_REVIEW", "RETURNED_TO_REQUESTER", "SOURCING"] as const;

export const MAX_REVIEW_NOTE_LENGTH = 4000;

export function canTransition(from: RequestStatus, to: RequestStatus): boolean {
  return REQUEST_TRANSITIONS[from].includes(to);
}

interface RequestFacts {
  requesterId: string;
  status: RequestStatus;
  legalEntityId: string | null;
  businessUnitId: string | null;
  assigneeId: string | null;
}

/**
 * Whether a user with this visibility (scopeFilter, ADR-0012) may see the request. A request that is
 * not the user's own is visible only once submitted and never while private (`PRIVATE_STATUSES`:
 * a draft, ADR-0022, or an abandoned draft, ADR-0024). Mirrors the API's database filter.
 */
export function canSeeRequest(visibility: RequestVisibility, userId: string, request: RequestFacts): boolean {
  const own = request.requesterId === userId;
  const shared = !PRIVATE_STATUSES.has(request.status);
  switch (visibility.kind) {
    case "none":
      return false;
    case "own":
      return own;
    case "all":
      return own || shared;
    case "scoped":
      return (
        (own && visibility.includeOwn) ||
        (shared &&
          ((request.legalEntityId !== null && visibility.legalEntityIds.includes(request.legalEntityId)) ||
            (request.businessUnitId !== null && visibility.businessUnitIds.includes(request.businessUnitId))))
      );
  }
}

export interface ReviewActions {
  /** Take an unassigned request for oneself. */
  canTake: boolean;
  /** Assign or reassign to any eligible buyer (procurement managers). */
  canAssign: boolean;
  /** Statuses this user may move the request to now. */
  transitions: RequestStatus[];
  /** Write internal comments. */
  canComment: boolean;
}

/**
 * What a reviewer may do with a request they can see (ADR-0022). Never on their own request
 * (segregation of duties); status changes by the assignee or a `requests.assign` holder; starting
 * the review of an unassigned request takes it.
 */
export function reviewActions(
  user: { id: string; permissions: readonly string[] },
  request: Pick<RequestFacts, "requesterId" | "status" | "assigneeId">,
): ReviewActions {
  const none: ReviewActions = { canTake: false, canAssign: false, transitions: [], canComment: false };
  const held = new Set(user.permissions);
  if (
    !held.has(PERMISSIONS.REQUESTS_REVIEW) ||
    request.requesterId === user.id ||
    PRIVATE_STATUSES.has(request.status)
  ) {
    return none;
  }
  const manager = held.has(PERMISSIONS.REQUESTS_ASSIGN);
  const mine = request.assigneeId === user.id;
  const unassigned = request.assigneeId === null;
  const final = request.status === "APPROVED" || request.status === "REJECTED";
  const transitions = REQUEST_TRANSITIONS[request.status].filter(
    (to) => manager || mine || (unassigned && request.status === "SUBMITTED" && to === "IN_REVIEW"),
  );
  return {
    canTake: unassigned && !final,
    canAssign: manager && !final,
    transitions,
    canComment: true,
  };
}

// ── API contracts ─────────────────────────────────────────────────────────────

const note = z.string().trim().max(MAX_REVIEW_NOTE_LENGTH);

export const transitionRequestSchema = z.object({
  to: z.enum(REQUEST_STATUSES),
  note: note.optional(),
});
export type TransitionRequest = z.infer<typeof transitionRequestSchema>;

export const assigneeRequestSchema = z.object({ userId: z.uuid().nullable() });
export type AssigneeRequest = z.infer<typeof assigneeRequestSchema>;

export const commentRequestSchema = z.object({ body: note.min(1) });
export type CommentRequest = z.infer<typeof commentRequestSchema>;

export const QUEUE_ASSIGNEE_FILTERS = ["any", "me", "unassigned"] as const;

export const queueQuerySchema = z.object({
  /** `open` = OPEN_WORK_STATUSES; `all` = every status after submission. */
  status: z.enum([...REQUEST_STATUSES, "open", "all"]).default("open"),
  assignee: z.enum(QUEUE_ASSIGNEE_FILTERS).default("any"),
  /** Reference (PR-000042 or 42), title or requester name; case-insensitive. */
  q: z.string().trim().max(100).optional(),
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type QueueQuery = z.infer<typeof queueQuerySchema>;

const person = z.object({ id: z.uuid(), displayName: z.string() });
const orgUnit = z.object({ code: z.string(), name: z.string() });

export const queueItemSchema = z.object({
  id: z.uuid(),
  reference: z.string(),
  title: z.string().nullable(),
  status: z.enum(REQUEST_STATUSES),
  requester: person,
  legalEntity: orgUnit.nullable(),
  businessUnit: orgUnit.nullable(),
  engagementShape: z.enum(ENGAGEMENT_SHAPES).nullable(),
  sizeTier: z.enum(SIZE_TIERS).nullable(),
  assignee: person.nullable(),
  submittedAt: z.string().nullable(),
  /** Open findings of severity WARN or HIGH. */
  warnings: z.number().int(),
});
export type QueueItem = z.infer<typeof queueItemSchema>;

export const queueResponseSchema = z.object({
  items: z.array(queueItemSchema),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
});
export type QueueResponse = z.infer<typeof queueResponseSchema>;

export const assigneeOptionSchema = z.object({ id: z.uuid(), displayName: z.string(), email: z.string() });
export type AssigneeOption = z.infer<typeof assigneeOptionSchema>;

/** Timeline entries: status changes, assignments and the stalled-request steps (ADR-0024). */
export const REQUEST_EVENT_KINDS = ["STATUS_CHANGED", "ASSIGNED", "REMINDER", "FOLLOW_UP"] as const;

export const requestEventSchema = z.object({
  id: z.uuid(),
  kind: z.enum(REQUEST_EVENT_KINDS),
  fromStatus: z.enum(REQUEST_STATUSES).nullable(),
  toStatus: z.enum(REQUEST_STATUSES).nullable(),
  /** ASSIGNED: the new assignee (null = unassigned). */
  assignee: person.nullable(),
  actor: person.nullable(),
  note: z.string().nullable(),
  createdAt: z.string(),
});
export type RequestEvent = z.infer<typeof requestEventSchema>;

export const commentViewSchema = z.object({
  id: z.uuid(),
  author: person.nullable(),
  body: z.string(),
  createdAt: z.string(),
});
export type CommentView = z.infer<typeof commentViewSchema>;

/** GET /api/requests/:id/review — everything a buyer needs on one page (never shown to the requester). */
export const reviewDetailSchema = requestPanelSchema.extend({
  messages: z.array(messageViewSchema),
  requester: person.extend({
    email: z.string(),
    /** From the HR directory when matched (ADR-0021). */
    company: z.string().nullable(),
    directoryBusinessUnit: z.string().nullable(),
    role: z.string().nullable(),
  }),
  legalEntity: orgUnit.nullable(),
  businessUnit: orgUnit.nullable(),
  assignee: person.nullable(),
  events: z.array(requestEventSchema),
  comments: z.array(commentViewSchema),
  actions: z.object({
    canTake: z.boolean(),
    canAssign: z.boolean(),
    transitions: z.array(z.enum(REQUEST_STATUSES)),
    canComment: z.boolean(),
  }),
});
export type ReviewDetail = z.infer<typeof reviewDetailSchema>;
